import { Typography } from '@mui/material'
import { action, autorun, observable, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { BaseFormPackageInstanceModel } from '../../api/DTOtemp'
import { FormPackageInstancesClient } from '../../api/clients/identity/FormPackageInstancesClient'
import {
	ForwardedFormPackageWorkflowTaskState,
	WorkflowTaskPermissions,
	WorkflowTaskResponse,
	WriteWorkflowTaskModel,
} from '../../api/clients/workflowTasks/DTOs'
import { FullscreenSpinner } from '../../components/feedback/circular'
import {
	DetailFormWithData,
	downloadFormPackageInstance,
	getFormPackageInstance,
} from '../../services/offline/FormPackageHandling'
import {
	InstanceCacheFormPackage,
	RetrievalStorageType,
} from '../../services/offline/FormPackageStorage'
import { getWorkflowTask } from '../../services/offline/WorkflowTaskHandling'
import { FormPackageControlApiContext } from './FormPackageControlApi'
import { PackageApi, PackageApiStatus } from './PackageApi'
import { PackageHost } from './PackageHost'
import { PackageHostErrorPage } from './PackageHostErrorPage'
import { PackageHostPageContextProvider } from './PackageHostPageContext'
import { useOfflinePackageContext } from './PackageListPageContext'

/**
 * for forwarded forms -- user can be logged in or anonymous
 * route is /_package-host/:packageId/_received/:workflowTaskId for logged in and anonymous
 */
export const ReceivedFormPackageHostPage = observer(() => {
	const params = useParams<{
		packageId: string
		workflowTaskId: string
	}>()

	const offlinePackageContext = useOfflinePackageContext()

	const packageId = parseInt(params.packageId)
	const workflowTaskId = parseInt(params.workflowTaskId)

	const localStore = useLocalObservable(() => ({
		storedPackage: undefined as InstanceCacheFormPackage | undefined,

		workflowTask: undefined as
			| WorkflowTaskResponse<ForwardedFormPackageWorkflowTaskState>
			| undefined,

		forbidden: false,
	}))

	if (isNaN(packageId)) throw new Error('invalid package id')
	if (isNaN(workflowTaskId)) throw new Error('invalid workflow task id')

	if (
		localStore.storedPackage !== undefined &&
		localStore.storedPackage.workflowTask === undefined
	) {
		throw new Error(
			'invariant failed - received form package workflow task was missing',
		)
	}

	const packageApi = useMemo(() => {
		return new PackageApi(packageId)
	}, [packageId])

	useEffect(() => {
		const abortController = new AbortController()

		async function fetchFormsData() {
			try {
				// note that if we're offline then this will get the cached
				// workflow task, so that means that if we're talking about  locks
				// we have to request the lock again for the form package
				const workflowTask = (await getWorkflowTask(
					workflowTaskId,
					WorkflowTaskPermissions.Write,
				)) as
					| WorkflowTaskResponse<ForwardedFormPackageWorkflowTaskState>
					| WriteWorkflowTaskModel<ForwardedFormPackageWorkflowTaskState>
					| undefined
				if (workflowTask === undefined)
					throw new Error(
						'unable to resolve workflow task with id ' + workflowTaskId,
					)

				const receivedItemIndex = offlinePackageContext.receivedPackageList.get(
					workflowTask.workflowTaskState.packageInstanceId.toString(),
				)

				let packageInstance: InstanceCacheFormPackage | undefined =
					await getFormPackageInstance(
						workflowTask.workflowTaskState.uniqueInstanceId,
					)

				if (navigator.onLine) {
					// TODO - possible error - where is the package instance id coming from?
					try {
						packageInstance = await downloadFormPackageInstance(
							RetrievalStorageType.Received,
							workflowTask.workflowTaskState.uniqueInstanceId,
							receivedItemIndex,
							abortController.signal,
							undefined,
							workflowTask,
						)
					} catch (error) {
						console.error(error)
					}
				} else if (packageInstance === undefined && !navigator.onLine) {
					console.error('form package is not downloaded in cache')
					runInAction(() => {
						localStore.forbidden = true
					})
					return
				}

				runInAction(() => {
					if (abortController.signal.aborted) return

					localStore.storedPackage = packageInstance
					localStore.workflowTask = workflowTask
					localStore.forbidden = false
				})
			} catch (e) {
				console.error(e)

				// we don't want to do anything about cancelled because
				// it could be the component re-mounting

				if (abortController.signal.aborted) {
					console.log(
						'ignoring error as cancelled status indicates method wil re-run',
					)
					return
				}

				runInAction(() => {
					localStore.forbidden = true
				})
			}
		}

		fetchFormsData()

		return () => {
			abortController.abort()
		}
	}, [packageId, workflowTaskId])

	useEffect(() => {
		// We need to update the workflow task id in the route so we don't get an
		// old workflow task when we refresh. This itself does not re-render the
		// page an extra time.
		const disposer = autorun(() => {
			if (localStore.workflowTask?.id === workflowTaskId) return

			window.history.replaceState(
				null,
				'', // according to mdn web docs this is traditionally empty (can't be null)
				`/_package-host/${packageId}/_received/${localStore.workflowTask?.id ?? workflowTaskId
				}`,
			)
		})

		return () => disposer()
	}, [])

	if (localStore.forbidden)
		return (
			<PackageHostErrorPage
				packageId={packageId}
				errorMessage={
					<>
						<Typography variant="h6">
							You don't have access to this package
						</Typography>{' '}
						<Typography variant="h6">or this package doesn't exist.</Typography>
					</>
				}
			/>
		)

	if (
		localStore.storedPackage === undefined ||
		localStore.workflowTask === undefined ||
		packageApi.status !== PackageApiStatus.Complete
	)
		return <FullscreenSpinner />

	// TODO - have to handle saved instance id

	const packageInstance = localStore.storedPackage

	return (
		<PackageHostPageContextProvider validationErrors={observable({})}>
			<FormPackageControlApiContext forms={packageInstance.forms}>
				<PackageHost
					packageId={packageId}
					packageApi={packageApi}
					formPackage={packageInstance.packageVersion}
					resources={packageInstance.resources}
					forms={packageInstance.forms}
					workflowTask={localStore.workflowTask}
					onUpdateWorkflowTask={action((workflowTask) => {
						localStore.workflowTask = workflowTask
					})}
					savedInstanceId={packageInstance.instanceId}
					onSaveInstance={handleCompleteApproval}
					isPackageFromServer={true}
				/>
			</FormPackageControlApiContext>
		</PackageHostPageContextProvider>
	)
})

// create new instance for approved/rejected package
export const handleCompleteApproval = async (
	baseFormPackageInstance: BaseFormPackageInstanceModel,
	forms: DetailFormWithData[],
): Promise<number> => {
	const instancesClient = new FormPackageInstancesClient()
	if (instancesClient === undefined)
		throw new Error('instances client is undefined')

	const { data: newInstance } = await instancesClient.CreateFormPackageInstance(
		baseFormPackageInstance,
		forms,
	)

	return newInstance.id
}
